/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import config from 'js/app/config';

import { Button, PageNotification, Typography2 } from '@coursera/cds-core';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import { InlineFeedbackRubricContext } from 'bundles/grading/components/staff-graded-assignments/form-parts/InlineFeedbackRubricContext';
import { hasUnsubmittedChanges, isRubricBoxView } from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

const styles = {
  container: css`
    position: relative;
    z-index: 4;

    .cds-localNotification-dismissible {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  nonDismissibleNotification: css`
    margin: 0 auto;
    margin-bottom: var(--cds-spacing-200);
  `,
};

// handles all local notifications that will appear relatively within a panel
const NotificationsPanel = () => {
  const { mutations, submissionData } = useContext(SGAPageContext);
  const { submission, submitter } = submissionData.data || {};

  const { forceSave, autoSaveTaskStates, reviewPartProgress } = useContext(AutoSaveContext);
  const { isReadOnly } = useContext(InlineFeedbackRubricContext);

  const currentAutoSaveTask = autoSaveTaskStates[submitter?.submitterId || ''];
  // ensure we give explanation to grader that there may be incomplete required rubrics when the grading status is finished but a warning icon is present
  // this occurs when a submission was graded before the required text-box rubric upgrade, and then a rubric is updated after that upgrade
  // still will cause legacy grader to recompute rubrics and retain a "draft-state" that is different from just unsubmitted changes, causing the warning icon in Assignment Grading page
  const incompleteRequiredRubricsAfterGradingComplete =
    hasUnsubmittedChanges(submitter?.submissionStatus, submitter?.isSubmissionGraded) &&
    !reviewPartProgress?.areRubricsComplete;

  // ensure unsubmittedChanges status is relevant to the current submitter in view
  const unsubmittedChanges =
    hasUnsubmittedChanges(submitter?.submissionStatus, submitter?.isSubmissionGraded) &&
    reviewPartProgress?.areRubricsComplete &&
    !currentAutoSaveTask?.isSubmitting;

  // determine whether the current rubric is an inline-feedback, and whether the submitter's grade is visible or not
  // in order to display an info notification about how to change update the grade
  const currentRubricIsReadOnlyInlineFeedback =
    !!submission?.currentRubric && isRubricBoxView(submission.currentRubric) && isReadOnly;

  if (
    !currentAutoSaveTask?.saveError &&
    !currentAutoSaveTask?.submitError &&
    !unsubmittedChanges &&
    !currentRubricIsReadOnlyInlineFeedback &&
    !incompleteRequiredRubricsAfterGradingComplete &&
    !mutations?.updateGradeVisibilityStatus?.error
  ) {
    return null;
  }

  let errorText;
  let errorTitle;
  let errorDetails;
  let hasConflict = false;
  let payloadTooLarge = false;

  if (currentAutoSaveTask?.saveError) {
    if (currentAutoSaveTask.saveError.graphQLErrors?.[0]?.extensions?.errorDetail === 'CONFLICT') {
      hasConflict = true;
    } else if (currentAutoSaveTask.saveError.graphQLErrors?.[0]?.extensions?.errorDetail === 'PAYLOAD_TOO_LARGE') {
      payloadTooLarge = true;
    } else {
      errorTitle = _t('Unable to save your work');
      errorText = _t('Something went wrong while saving your work. Please contact support.');
      errorDetails = currentAutoSaveTask.saveError.message;
    }
  } else if (currentAutoSaveTask?.submitError) {
    errorTitle = _t('Unable to submit to gradebook');
    errorText = _t('Something went wrong while submitting to gradebook. Please contact support.');
    errorDetails = currentAutoSaveTask.submitError.message;
  } else if (mutations?.updateGradeVisibilityStatus?.error) {
    errorTitle = _t('Unable to update grade visibility');
    errorText = _t('Something went wrong while updating grade visibility. Please try again.');
    errorDetails = mutations.updateGradeVisibilityStatus.error?.message;
  }

  return (
    <div css={styles.container} data-testid="grading-notifications-panel-container">
      {hasConflict && (
        <PageNotification
          severity="error"
          title={_t('Someone else is grading this submission')}
          inlineAction={
            <Button size="small" variant="ghost" onClick={() => window.location.reload()}>
              {_t('Reload data')}
            </Button>
          }
        >
          {_t(
            `Your changes can't be saved because someone else is grading this learner. Reload data to view their changes.`
          )}
        </PageNotification>
      )}
      {payloadTooLarge && (
        <PageNotification severity="error" title={_t('Comment too large')} css={styles.nonDismissibleNotification}>
          {_t(`Your changes can't be saved because the comment is too large.`)}
        </PageNotification>
      )}
      {unsubmittedChanges && (
        <PageNotification
          severity="warning"
          title={_t('Warning')}
          inlineAction={
            <Button
              size="small"
              variant="ghost"
              disabled={currentAutoSaveTask?.isSaving || currentAutoSaveTask?.isSubmitting}
              onClick={forceSave}
            >
              {_t('Submit to gradebook')}
            </Button>
          }
        >
          {_t(`Grading was finished, but there are unsubmitted changes.`)}
        </PageNotification>
      )}
      {incompleteRequiredRubricsAfterGradingComplete && (
        <PageNotification severity="information" css={styles.nonDismissibleNotification}>
          {_t(`There are some incomplete required rubrics remaining.`)}
        </PageNotification>
      )}
      {currentRubricIsReadOnlyInlineFeedback && (
        <PageNotification severity="information" css={styles.nonDismissibleNotification}>
          {_t('Grade visibility must be hidden to give inline feedback.')}
        </PageNotification>
      )}
      {errorText && (
        <PageNotification
          inlineAction={
            <Button size="small" variant="ghost" onClick={() => window.location.reload()}>
              {_t('Reload page')}
            </Button>
          }
          severity="error"
          title={errorTitle}
        >
          <div>
            {errorText}

            {/* Displaying error details for faster troubleshoot during initial release; Will only render in development environment */}
            {errorDetails && config.environment === 'development' && (
              <Typography2 component="div" variant="bodySecondary">
                Details: {errorDetails}
              </Typography2>
            )}
          </div>
        </PageNotification>
      )}
    </div>
  );
};

export default NotificationsPanel;
